import React, { Suspense, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Drawer, IconButton, Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    AccountTreeTwoTone,
    CloseTwoTone,
    CollectionsBookmarkTwoTone,
    EmailTwoTone,
    GroupsTwoTone,
    SettingsApplicationsTwoTone,
    MeetingRoomTwoTone,
    BusinessTwoTone,
    ExtensionTwoTone,
    AdminPanelSettingsTwoTone,
    SupervisedUserCircleTwoTone,
    IntegrationInstructionsTwoTone
} from '@mui/icons-material';
import clsx from 'clsx';
import NewUINavItem from '../NewUINavItem';
import { ConfirmProvider } from 'material-ui-confirm';
import useLocalStore from '../../useLocalStore';
import { useAuth } from '../../contexts/AuthContext';
import { Sidebar as ReactProSidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import leftArrow from 'assets/icons/main/arrow_menu_close.svg';
import rightArrow from 'assets/icons/main/arrow_menu_open.svg';
import SidebarRightToLeft from 'components/SidebarRightToLeft';
import logoShort from 'assets/icons/main/el_logo_icon.svg';
import logoFull from 'assets/icons/main/el_logo_full.svg';
import OrganizationSelector from 'components/OrganizationSelector';

const appMenuDrawerWidth = 250;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    width: appMenuDrawerWidth,
    minWidth: 50,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    position: 'relative',
    '&.drawerOpen': {
        width: appMenuDrawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        })
    },
    '&.drawerClose': {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        overflowX: 'hidden',
        width: theme.spacing(8) + 1,
        [theme.breakpoints.up('sm')]: {
            width: 79
        }
    }
}));

const StyledMain = styled('main')(({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(4),
    maxWidth: '1480px',
    '&.drawer_open': {
        // Add any specific styles for when drawer is open
    }
}));

const fullscreen = ['Dashboard', 'CreateJourney', 'EditCampaigns'];
const noMaxWidth = ['AdminActiveCampaigns'];
const sidebarsWithCustomWidth = ['SingleAudience', 'CampaignStatistics', 'CreateJourney', 'Dashboard', 'CampaignsAll', 'Audience'];

const Layout = ({ main, pageName }) => {
    const history = useHistory();
    const { logout, currentKratosSession, organizations, currentOrganization, currentIdentity } = useAuth();
    const [getSidebar, resetSidebar] = useLocalStore((state) => [state.getSidebar, state.resetSidebar]);
    const [appMenuCollapsed, setAppMenuCollapsed] = useState(false);
    const [orgSelectorOpen, setOrgSelectorOpen] = useState(false);
    const sidebar = getSidebar();

    const userEmail = currentKratosSession?.identity?.traits?.email;
    // TODO
    // const orgName = currentOrganization?.name;

    const toggleAppMenuCollapsed = () => setAppMenuCollapsed((state) => !state);
    // const hideSidebar = async () => resetSidebar();
    const handleLogout = async () => {
        console.log('logout with auth.logoutAction');
        await logout();
    };

    return (
        <ConfirmProvider>
            <Box sx={{ display: 'flex' }}>
                <StyledDrawer
                    variant='permanent'
                    className={clsx({
                        drawerOpen: !appMenuCollapsed,
                        drawerClose: appMenuCollapsed
                    })}
                >
                    <div className={clsx('toggleButton', {
                        drawerOpen: !appMenuCollapsed,
                        drawerClose: appMenuCollapsed
                    })} onClick={toggleAppMenuCollapsed}>
                        <img src={!appMenuCollapsed ? leftArrow : rightArrow} alt='toggle' style={{ width: 10, height: 10, zIndex:999999 }} />
                    </div>
                    <ReactProSidebar
                        collapsed={appMenuCollapsed}
                        className={clsx('reactProSidebar', {
                            drawerOpen: !appMenuCollapsed,
                            drawerClose: appMenuCollapsed
                        })}
                        width={`${appMenuDrawerWidth}px`}
                    >
                        <div className={clsx('sidebarWrapper', {
                            drawerOpen: !appMenuCollapsed,
                            drawerClose: appMenuCollapsed
                        })}>
                            <div className={clsx('logoContainer', {
                                drawerOpen: !appMenuCollapsed,
                                drawerClose: appMenuCollapsed
                            })}>
                                <img
                                    src={!appMenuCollapsed ? logoFull : logoShort}
                                    alt='Logo'
                                    className={clsx('logo', {
                                        drawerOpen: !appMenuCollapsed,
                                        drawerClose: appMenuCollapsed
                                    })}
                                    onClick={toggleAppMenuCollapsed}
                                />
                            </div>
                            <div className={clsx('mainMenu', {
                                drawerOpen: !appMenuCollapsed,
                                drawerClose: appMenuCollapsed
                            })}>
                                <Menu>
                                    <MenuItem icon={<EmailTwoTone/>} component={<Link to='/campaigns'/>}>
                                        Campaigns
                                    </MenuItem>
                                    <MenuItem icon={<AccountTreeTwoTone/>} component={<Link to='/journeys/active'/>}>
                                        Journeys
                                    </MenuItem>
                                    <SubMenu 
                                        label='Profiles' 
                                        icon={<GroupsTwoTone/>}
                                    >
                                        <MenuItem component={<Link to='/audiences'/>}>Audiences</MenuItem>
                                        <MenuItem component={<Link to='/import-sessions'/>}>Import</MenuItem>
                                        <MenuItem component={<Link to='/profile-props'/>}>Properties</MenuItem>
                                        <MenuItem component={<Link to='/profiles/events'/>}>Events</MenuItem>
                                        <MenuItem component={<Link to='/profiles/event-types'/>}>Event Types</MenuItem>
                                        <MenuItem component={<Link to='/event-queries'/>}>Event Queries</MenuItem>
                                    </SubMenu>
                                    <SubMenu 
                                        label='Library' 
                                        icon={<CollectionsBookmarkTwoTone/>}
                                    >
                                        <MenuItem component={<Link to='/library'/>}>Templates</MenuItem>
                                    </SubMenu>
                                    <SubMenu 
                                        label='Setup' 
                                        icon={<SettingsApplicationsTwoTone/>}
                                    >
                                        <MenuItem component={<Link to='/settings'/>}>Settings</MenuItem>
                                        <MenuItem component={<Link to='/settings/senders'/>}>Senders</MenuItem>
                                        {/* <MenuItem component={<Link to='/vmta-pools'/>}>Send Pools</MenuItem> */}
                                        {/* <MenuItem component={<Link to='/email-sender-profiles'/>}>Email Sender</MenuItem>
                                        <MenuItem component={<Link to='/email-sending-domains'/>}>Sending Domain</MenuItem> */}
                                    </SubMenu>
                                    <SubMenu 
                                        label='Integrations'
                                        icon={<ExtensionTwoTone/>}
                                    >
                                        <MenuItem component={<Link to='/integrations' />}>API Keys</MenuItem>
                                        <MenuItem component={<Link to='/admin/pixel-actions' />}>Pixels</MenuItem>
                                        {/* <MenuItem component={<Link to='/forms' />}>Forms</MenuItem> */}
                                    </SubMenu>
                                    <SubMenu 
                                        label='Admin'
                                        icon={<AdminPanelSettingsTwoTone/>}
                                    >

                                        <MenuItem component={<Link to='/admin/delivery-overview' />}>Delivery Overview</MenuItem>
                                        <MenuItem component={<Link to='/admin/util/toolbox' />}>Toolbox</MenuItem>
                                        <MenuItem component={<Link to='/upload-process-sessions' />}>Key Suppression</MenuItem>
                                        <MenuItem component={<Link to='/users' />}>Users</MenuItem>
                                    </SubMenu>
                                    {currentOrganization?.organizer && (
                                        <SubMenu 
                                            label='Organizer' 
                                            icon={<BusinessTwoTone/>}
                                        >
                                            <MenuItem component={<Link to='/organizer/descendant-organizations'/>}>Organizations</MenuItem>
                                            <MenuItem component={<Link to='/organizer/config'/>}>Defaults</MenuItem>
                                        </SubMenu>
                                    )}
                                </Menu>
                            </div>
                            <div style={{ 
                                height: '1px', 
                                backgroundColor: '#e0e0e0', 
                                margin: '8px 16px',
                                opacity: 0.5
                            }} />
                            <div className={clsx('bottomMenu', {
                                drawerOpen: !appMenuCollapsed,
                                drawerClose: appMenuCollapsed
                            })}>
                                <Menu>
                                    <div style={{ 
                                        padding: '4px 16px 2px 16px',
                                        fontSize: '10px',
                                        color: '#666',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Current Organization
                                    </div>
                                    <MenuItem className={clsx('organizationMenuItem', {
                                        drawerOpen: !appMenuCollapsed,
                                        drawerClose: appMenuCollapsed
                                    })}>
                                        <Box 
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                width: '100%',
                                                gap: 1
                                            }}
                                        >
                                            <Box 
                                                component={Link}
                                                to="/my-organization"
                                                sx={{ 
                                                    flex: 1,
                                                    cursor: 'pointer',
                                                    textDecoration: 'none',
                                                    color: 'inherit',
                                                    minWidth: 0, // Allow text to truncate
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    '&:hover': {
                                                        opacity: 0.8
                                                    }
                                                }}
                                            >
                                                {currentOrganization?.name}
                                            </Box>
                                            {organizations && organizations.length > 1 && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setOrgSelectorOpen(true)}
                                                    sx={{
                                                        minWidth: 'auto',
                                                        padding: '2px 8px',
                                                        fontSize: '10px',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        borderColor: '#ccc',
                                                        color: '#666',
                                                        flexShrink: 0, // Prevent button from shrinking
                                                        '&:hover': {
                                                            borderColor: '#999',
                                                            backgroundColor: '#f5f5f5'
                                                        }
                                                    }}
                                                >
                                                    Change
                                                </Button>
                                            )}
                                        </Box>
                                    </MenuItem>
                                    <div style={{ 
                                        padding: '4px 16px 2px 16px',
                                        fontSize: '10px',
                                        color: '#666',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Logged in as
                                    </div>
                                    <MenuItem component={<Link to='/users/me' />} >{userEmail}</MenuItem>
                                    {currentIdentity?.is_super_user && (
                                        <>
                                            <div style={{ 
                                                height: '1px', 
                                                backgroundColor: '#e0e0e0', 
                                                margin: '8px 16px',
                                                opacity: 0.5
                                            }} />
                                            <div style={{
                                                backgroundColor: '#e3f2fd', // Light blue background
                                                borderRadius: '4px',
                                                margin: '4px 8px',
                                                border: '1px solid #bbdefb'
                                            }}>
                                                <SubMenu 
                                                    label='Super User'
                                                    style={{
                                                        backgroundColor: 'transparent'
                                                    }}
                                                >
                                                    <NewUINavItem
                                                        label="Campaigns ( active )"
                                                        path="/su/campaigns/active"
                                                        style={{ backgroundColor: 'transparent' }}
                                                    />
                                                    <NewUINavItem
                                                        label="Variants ( sendable )"
                                                        path="/su/variants/sendable"
                                                        style={{ backgroundColor: 'transparent' }}
                                                    />
                                                    <MenuItem 
                                                        component={<Link to='/admin/ip-addresses' />}
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        IP Inventory
                                                    </MenuItem>
                                                    <MenuItem 
                                                        component={<Link to='/admin/vmtas' />}
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        Vmtas + Pools
                                                    </MenuItem>
                                                    <MenuItem 
                                                        component={<Link to='/dashboard'/>}
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        Delivery
                                                    </MenuItem>
                                                    <MenuItem 
                                                        component={<Link to='/admin/pmta/configs'/>}
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        PMTA Configs
                                                    </MenuItem>
                                                    <MenuItem
                                                        component={<Link to='/admin/caddy/configs'/>}
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        Caddy Configs
                                                    </MenuItem>
                                                    <MenuItem
                                                        component={<Link to='/admin/kumo/configs'/>}
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        Kumo Configs
                                                    </MenuItem>
                                                    <MenuItem
                                                        component={<Link to='/admin/config'/>}
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        Configs
                                                    </MenuItem>

                                                    <NewUINavItem
                                                        label="Optizmo"
                                                        path="/su/suppression/optizmo"
                                                        oldPath="/admin/suppression-lists/optizmo"
                                                        style={{ backgroundColor: 'transparent' }}
                                                    />

                                                    <NewUINavItem
                                                        label="Organizers"
                                                        path="/su/organizers"
                                                        oldPath="/admin/organizers"
                                                        style={{ backgroundColor: 'transparent' }}
                                                    />
                                                    <NewUINavItem
                                                        label="Organizations"
                                                        path="/su/organizations"
                                                        oldPath="/admin/organizations"
                                                        style={{ backgroundColor: 'transparent' }}
                                                    />
                                                    <MenuItem 
                                                        component={<Link to='/admin/email-sending-domains'/>} 
                                                        style={{backgroundColor: 'transparent' }}
                                                    >
                                                        Email Sending Domains
                                                    </MenuItem>
                                                    <MenuItem 
                                                        component={<Link to='/admin/email-sender-profiles'/>} 
                                                        style={{backgroundColor: 'transparent' }}
                                                    >
                                                        Email Sender Profiles
                                                    </MenuItem>
                                                    <MenuItem 
                                                        component={<Link to='/admin/outgoing-email-services'/>} 
                                                        style={{backgroundColor: 'transparent' }}
                                                    >
                                                        Outgoing Email Services
                                                    </MenuItem>

                                                    <MenuItem
                                                        component={<Link to='/admin/audience-snapshots'/>}
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        Audience Snapshots
                                                    </MenuItem>

                                                    <SubMenu
                                                        label='Debug'
                                                        style={{ backgroundColor: 'transparent' }}
                                                    >
                                                        <MenuItem 
                                                            component={<Link to='/admin/debug/campaign-counts'/>}
                                                            style={{ backgroundColor: 'transparent' }}
                                                        >
                                                            Campaign Counts
                                                        </MenuItem>
                                                    </SubMenu>

                                                </SubMenu>
                                            </div>
                                            <div style={{ 
                                                height: '1px', 
                                                backgroundColor: '#e0e0e0', 
                                                margin: '8px 16px',
                                                opacity: 0.5
                                            }} />
                                        </>
                                    )}
                                    <MenuItem icon={<MeetingRoomTwoTone/>} onClick={handleLogout}>
                                        Logout
                                    </MenuItem>
                                </Menu>
                            </div>
                        </div>
                    </ReactProSidebar>
                </StyledDrawer>
                <StyledMain
                    className={clsx({
                        drawer_open: !appMenuCollapsed
                    })}
                >
                    <Suspense fallback={null}>{main}</Suspense>
                </StyledMain>
                <Drawer
                    anchor='right'
                    open={sidebar?.open ?? false}
                    onClose={resetSidebar}
                    disableEnforceFocus
                    style={{ zIndex: 1350 }}
                >
                    <Box className='context__custom-width context'>
                        <IconButton 
                            size='small' 
                            onClick={resetSidebar}
                            className='context-close'
                        >
                            <CloseTwoTone />
                        </IconButton>
                        <SidebarRightToLeft page={pageName} />
                    </Box>
                </Drawer>
                <OrganizationSelector 
                    open={orgSelectorOpen} 
                    onClose={() => setOrgSelectorOpen(false)} 
                />
            </Box>
        </ConfirmProvider>
    );
};

export default Layout;